import {Component, Injector, OnInit, OnDestroy, ViewChild} from "@angular/core";
import {createDtoProperties, DtoComponent} from "@mango/core";
import {Visitor} from "../visitor/visitor";
import {VisitorService} from "../visitor/visitor.service";
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatStepper} from "@angular/material/stepper";
import {Observable} from "rxjs";
import {BooleanInput} from "@angular/cdk/coercion";
import {map, startWith} from "rxjs/operators";
import {Company} from "../visitor/company";
import {CompanyService} from "../visitor/company.service";
import {School} from "../visitor/school";
import {SchoolService} from "../visitor/school.service";

@Component({
  selector: "app-register",
  templateUrl: "./register.component.html",
  styleUrls: ["./register.component.css"],
})
export class RegisterComponent extends DtoComponent<Visitor> implements OnInit, OnDestroy {
  @ViewChild("stepper") stepper: MatStepper;
  randomNumber: number;
  firstFormGroup: FormGroup;
  secondFormGroup: FormGroup;
  selectedOption: string;
  countdown = 30; // 30 seconds until it automatically closes
  filteredOptions: Observable<string[]>;
  companyNames: string[] = [];
  schoolNames: string[] = [];
  backgroundPictureCount = 8;
  interval: any;
  submitting = false;
  isEditable: BooleanInput = true;
  data = this.visitorService.getVisitors();

  properties = createDtoProperties<Visitor>([
    "name",
    {"company": ["id"]},
    "meetingPerson",
    "totalDurationMin",
    "registerTime",
    "unregisterTime",
    {"school": ["id"]},
    "other",
  ]);
  thirdFormGroup: FormGroup;

  constructor(
    private visitorService: VisitorService,
    private companyService: CompanyService,
    private schoolService: SchoolService,
    injector: Injector,
    private snackBar: MatSnackBar,
    private _formBuilder: FormBuilder,
    protected router: Router,
  ) {
    super(injector, visitorService, Visitor);
    this.randomNumber = Math.floor(Math.random() * this.backgroundPictureCount) + 1;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.initializeForms();
    this.loadVisitorData();
    this.updateFilteredOptions();
    this.handleRoute("id");
  }

  private initializeForms(): void {
    this.secondFormGroup = this._formBuilder.group({
      visitorName: ["", [Validators.required, Validators.maxLength(50)]],
      visitorMeeting: ["", [Validators.required, Validators.maxLength(50)]],
      visitorCompany: [""],
      visitorSchool: [""],
      visitorOther: [""],
    });

    this.firstFormGroup = this._formBuilder.group({
      visitorCompany: [""],
      visitorSchool: [""],
      visitorOther: [""]
    });

    this.firstFormGroup.setValidators(this.noOptionSelectedValidator);
  }

  private noOptionSelectedValidator(group: FormGroup): { [key: string]: boolean } | null {
    const hasValue = Object.values(group.controls).some(control => control.value);
    return hasValue ? null : {noOptionSelected: true};
  }

  private async loadVisitorData(): Promise<void> {
    try {
      const data: Visitor[] = await this.visitorService.getVisitors();
      this.companyNames = this.extractNames(data, "company.name");
      this.schoolNames = this.extractNames(data, "school.name");
    } catch (error) {
      console.error("Failed to load visitor data:", error);
    }
  }

  private extractNames(visitors: Visitor[], field: "company.name" | "school.name"): string[] {
    return visitors
      .map(visitor => field === "company.name" ? visitor.company?.name : visitor.school?.name)
      .filter(Boolean);
  }

  private _filter(value: string): string[] {
    if (!value || value.length < 2) {
      return [];
    }
    const filterValue = value.toLowerCase();
    const options = this.selectedOption === "company" ? this.companyNames : this.schoolNames;
    const filteredResults = options.filter(option => option.toLowerCase().includes(filterValue));
    return Array.from(new Set(filteredResults));
  }

  selectOption(option: string): void {
    this.selectedOption = option;
    this.secondFormGroup.reset();

    this.firstFormGroup.patchValue({
      visitorCompany: option === "company" ? "selected" : "",
      visitorSchool: option === "school" ? "selected" : "",
      visitorOther: option === "other" ? "selected" : ""
    });

    this.firstFormGroup.updateValueAndValidity();
    this.updateFilteredOptions();
    this.stepper.next();
  }

  private updateFilteredOptions(): void {
    const controlName = this.selectedOption === "company" ? "visitorCompany" : "visitorSchool";
    this.filteredOptions = this.secondFormGroup.controls[controlName].valueChanges.pipe(
      startWith(""),
      map(value => this._filter(value))
    );
  }

  startCountdown(): void {
    clearInterval(this.interval);
    this.countdown = 30;
    this.interval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        clearInterval(this.interval);
        this.goBack(0);
      }
    }, 1000);
  }

  showSuccessMessage(message: string): void {
    this.snackBar.open(message, "Close", {
      duration: 10000,
      panelClass: ["success-snackbar"],
    });
  }

  showErrorMessage(message: string): void {
    this.snackBar.open(message, "Close", {
      duration: 10000,
      panelClass: ["error-snackbar"],
    });
  }

  async onSubmit(): Promise<void> {
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    this.isEditable = false;

    const {visitorName, visitorMeeting, visitorCompany, visitorSchool, visitorOther} = this.secondFormGroup.value;
    const selectedEntityName = this.secondFormGroup.value[`visitor${this.selectedOption.charAt(0).toUpperCase() + this.selectedOption.slice(1)}`];

    if (this.secondFormGroup.invalid || !selectedEntityName) {
      this.handleFormErrors();
      this.resetFormState();
      return;
    }

    const newGuest = new Visitor();
    newGuest.name = visitorName;
    newGuest.meetingPerson = visitorMeeting;
    newGuest.registerTime = new Date();

    try {
      if (this.selectedOption !== "other") {
        const selectedObject = (await this.data).find(item => item[this.selectedOption]?.name === selectedEntityName);
        if (selectedObject) {
          newGuest[this.selectedOption] = {id: selectedObject[this.selectedOption].id};
        } else {
          newGuest[this.selectedOption] = await this.createEntity(selectedEntityName);
        }
      } else {
        newGuest.other = visitorOther;
      }

      await this.visitorService.create(newGuest).toPromise();
      this.showSuccessMessage("Vergeet niet om u af te melden wanneer u vertrekt.");
      this.startCountdown();
      this.stepper.next();
    } catch (error) {
      console.error("Error submitting: ", error);
    } finally {
      this.resetFormState();
    }
  }

  private handleFormErrors(): void {
    const errorMessages = {
      visitorName: "Error: Uw naam ontbreekt!",
      visitorMeeting: "Error: De naam van de persoon die u ontmoet ontbreekt!",
      maxlengthName: "Error: Uw naam is te lang! (50 karakters maximum)",
      visitorCompany: "Error: U heeft geen bedrijf ingevuld!",
      visitorSchool: "Error: U heeft geen school ingevuld!",
      visitorOther: "Error: U heeft geen andere reden ingevuld!",
      maxlengthMeeting: "Error: De naam van de persoon die u ontmoet is te lang! (50 karakters maximum)",
      default: "Error submitting: unknown issue"
    };

    const controlErrors = this.secondFormGroup.controls;
    let errorMessage = errorMessages.default;

    for (const [controlName, control] of Object.entries(controlErrors)) {
      if (control.errors) {
        errorMessage = control.errors.maxlength
          ? errorMessages[`maxlength${controlName.charAt(0).toUpperCase() + controlName.slice(1)}`]
          : errorMessages[controlName];
        break;
      }
    }

    this.showErrorMessage(errorMessage);
  }

  private async createEntity(entityName: string): Promise<{ name: string; id: number }> {
    const entityService = this.selectedOption === "company" ? this.companyService : this.schoolService;
    const EntityClass = this.selectedOption === "company" ? Company : School;
    const newEntity = new EntityClass();
    newEntity.name = entityName;

    try {
      const createdEntity = await entityService.create(newEntity).toPromise();
      return {name: entityName, id: createdEntity.id};
    } catch (error) {
      this.showErrorMessage(`Error creating new ${this.selectedOption}. Please try again.`);
      throw error;
    }
  }

  private resetFormState(): void {
    this.submitting = false;
    this.isEditable = true;
  }

  goBack(stepIndex: number) {
    if (stepIndex === 0) {
      clearInterval(this.interval);
      this.router.navigate(["/landing"]);
    } else {
      this.secondFormGroup.reset();
      this.selectedOption = "";
      this.updateFilteredOptions();
      this.stepper.previous();
    }
  }
}
